import { useMapsStore } from '@components/shared/Maps/store';
import { getAddressFromLatLng } from '@components/shared/Maps/utils';
import { Map, MapMouseEvent, Marker } from '@vis.gl/react-google-maps';
import { useCallback, useEffect, useState } from 'react';

interface MapComponentProps {
	disabled?: boolean;
	initialCenter: google.maps.LatLngLiteral;
	position: google.maps.LatLngLiteral;
	onMarkerClick?: () => void;
	onMapClick?: (position: google.maps.LatLngLiteral, address: string) => void;
}

export function MapComponent({
	disabled = false,
	initialCenter,
	position,
	onMarkerClick,
	onMapClick,
}: MapComponentProps) {
	const [markerPosition, setMarkerPosition] = useState<google.maps.LatLngLiteral>(initialCenter);
	const { setAddress, setPosition } = useMapsStore();
	const [center, setCenter] = useState<google.maps.LatLngLiteral>(initialCenter);

	useEffect(() => {
		if (position) {
			const newPosition = {
				lat: position.lat,
				lng: position.lng,
			};

			setMarkerPosition(newPosition);
			setCenter(newPosition); // Add this line to update map center
			setPosition(newPosition);
		} else {
			setMarkerPosition(initialCenter);
			setCenter(initialCenter); // Add this line to update map center
			setPosition(initialCenter);
		}
	}, [initialCenter, position, setPosition]);

	const handleMapClick = useCallback(
		async (event: MapMouseEvent) => {
			if (disabled) return;
			const { latLng } = event.detail;

			if (!latLng) return;

			const newPosition = { lat: latLng.lat, lng: latLng.lng };

			setMarkerPosition(newPosition);

			const address = await getAddressFromLatLng(newPosition);

			setAddress(address);
			setPosition(newPosition);

			if (onMapClick) onMapClick(newPosition, address);
		},
		[setAddress, setPosition, onMapClick, disabled]
	);

	const handleMarkerDragEnd = useCallback(
		async (event: google.maps.MapMouseEvent) => {
			if (disabled) return;
			const latLng = event.latLng;

			if (!latLng) return;

			const newPosition = { lat: latLng.lat(), lng: latLng.lng() };

			setMarkerPosition(newPosition);

			const address = await getAddressFromLatLng(newPosition);

			setAddress(address);
			setPosition(newPosition);

			if (onMapClick) onMapClick(newPosition, address);
		},
		[setAddress, setPosition, onMapClick, disabled]
	);

	return (
		<Map
			onClick={handleMapClick}
			mapId="bf51a910020fa25a"
			defaultZoom={17}
			defaultCenter={center}
			mapTypeControl={true}
			gestureHandling={disabled ? 'none' : 'greedy'}
			streetViewControl={false}
			mapTypeId={google.maps.MapTypeId.SATELLITE}
			mapTypeControlOptions={{
				mapTypeIds: [
					google.maps.MapTypeId.ROADMAP,
					google.maps.MapTypeId.SATELLITE,
					google.maps.MapTypeId.HYBRID,
				],
			}}
		>
			<Marker
				onDragEnd={handleMarkerDragEnd}
				position={markerPosition}
				draggable={!disabled}
				clickable={!disabled}
				onClick={onMarkerClick}
				title="Clickable Marker"
			/>
		</Map>
	);
}
